import * as i0 from '@angular/core';
import { Injectable, NgModule, Optional, SkipSelf } from '@angular/core';
import { throwError } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import * as Forge from 'node-forge';
import * as i2 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';

var MFAType;
(function (MFAType) {
    MFAType["SOFTWARE"] = "SOFTWARE";
    MFAType["SMS"] = "SMS";
})(MFAType || (MFAType = {}));
var UserAttributeType;
(function (UserAttributeType) {
    UserAttributeType["EMAIL"] = "email";
    UserAttributeType["PHONE_NUMBER"] = "phone_number";
    UserAttributeType["GIVEN_NAME"] = "given_name";
    UserAttributeType["FAMILY_NAME"] = "family_name";
})(UserAttributeType || (UserAttributeType = {}));
var TokenNames;
(function (TokenNames) {
    TokenNames["ACCESS_TOKEN"] = "accessToken";
    TokenNames["ACCESS_TOKEN_PAYLOAD"] = "atp";
    TokenNames["REFRESH_TOKEN"] = "refreshToken";
})(TokenNames || (TokenNames = {}));

// tslint:disable
class TokenHelper {
    static setAccessToken(token) {
        localStorage.setItem(TokenNames.ACCESS_TOKEN, token);
    }
    static setAccessTokenPayload(payload) {
        localStorage.setItem(TokenNames.ACCESS_TOKEN_PAYLOAD, JSON.stringify(payload));
    }
    static getAccessToken() {
        return localStorage.getItem(TokenNames.ACCESS_TOKEN) || null;
    }
    static setRefreshToken(token) {
        localStorage.setItem(TokenNames.REFRESH_TOKEN, token);
    }
    static getRefreshToken() {
        return localStorage.getItem(TokenNames.REFRESH_TOKEN) || null;
    }
    static getAccessTokenPayload() {
        const accessTokenPayload = localStorage.getItem(TokenNames.ACCESS_TOKEN_PAYLOAD);
        const parsedToken = accessTokenPayload && accessTokenPayload !== 'undefined'
            && accessTokenPayload !== 'null' ? TokenHelper.parseAccessTokenPayload(accessTokenPayload) : null;
        return parsedToken;
    }
    static parseAccessTokenPayload(accessTokenPayload) {
        let payload = null;
        try {
            payload = JSON.parse(accessTokenPayload);
        }
        catch (err) {
            console.error('Failed to parse atp');
        }
        return payload;
    }
    static isAccesTokenValid() {
        const token = TokenHelper.getAccessTokenPayload();
        if (!token) {
            return false;
        }
        const tokenExpiry = token.exp ? +token.exp : 0;
        const thousand = 1000;
        const now = Date.now() / thousand;
        return tokenExpiry > now;
    }
    static shouldRefreshToken() {
        const token = TokenHelper.getAccessTokenPayload(), shouldRefreshSeconds = 600;
        if (!token)
            return false;
        const tokenExpiry = token.exp ? +token.exp : 0, thousand = 1000, now = Date.now() / thousand;
        return tokenExpiry - now < shouldRefreshSeconds;
    }
    static clearLocalStorage() {
        localStorage.removeItem(TokenNames.ACCESS_TOKEN);
        localStorage.removeItem(TokenNames.REFRESH_TOKEN);
        localStorage.removeItem(TokenNames.ACCESS_TOKEN_PAYLOAD);
    }
    // TODO: deprecated, to be removed once new token encryption is stable
    static parseJwt(token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => 
        // tslint:disable-next-line:no-magic-numbers
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
        return JSON.parse(jsonPayload);
    }
}

class EnvironmentConfig {
    constructor() {
        this.BASE_URL_BAMS = '';
    }
}

var UsersAction;
(function (UsersAction) {
    UsersAction["GET_HASH_SALT"] = "GET_HASH_SALT";
    UsersAction["LOGIN"] = "LOGIN";
    UsersAction["GLOBAL_LOG_OUT"] = "GLOBAL_LOG_OUT";
    UsersAction["SIGNUP"] = "SIGNUP";
    UsersAction["GET_ATTRIBUTE_VERIFICATION_CODE"] = "GET_ATTRIBUTE_VERIFICATION_CODE";
    UsersAction["ASSOCIATE_SOFTWARE_MFA"] = "ASSOCIATE_SOFTWARE_MFA";
    UsersAction["VERIFY_SOFTWARE_MFA"] = "VERIFY_SOFTWARE_MFA";
    UsersAction["MFA_OPTIONS"] = "MFA_OPTIONS";
    UsersAction["MFA_PREFERENCE"] = "MFA_PREFERENCE";
    UsersAction["MFA_CHALLENGE"] = "MFA_CHALLENGE";
    UsersAction["FORGOT_PASSWORD_INITIATE"] = "FORGOT_PASSWORD_INITIATE";
    UsersAction["FORGOT_PASSWORD_CONFIRM"] = "FORGOT_PASSWORD_CONFIRM";
    UsersAction["CHANGE_PASSWORD"] = "CHANGE_PASSWORD";
    UsersAction["REFRESH_SESSION"] = "REFRESH_SESSION";
    UsersAction["UPDATE_USER_ATTRIBUTE"] = "UPDATE_USER_ATTRIBUTE";
    UsersAction["VERIFY_USER_ATTRIBUTE"] = "VERIFY_USER_ATTRIBUTE";
    UsersAction["GET_CURRENT_USER"] = "GET_CURRENT_USER";
    UsersAction["RESEND_TEMP_PASSWORD"] = "RESEND_TEMP_PASSWORD";
})(UsersAction || (UsersAction = {}));
class UsersGatewayService {
    constructor(environment, http) {
        this.environment = environment;
        this.http = http;
        this.baseUrl = environment.BASE_URL_BAMS;
    }
    static setLocalStorageItems(response) {
        if (response.accessToken) {
            TokenHelper.setAccessToken(response.accessToken.jwtToken);
            TokenHelper.setRefreshToken(response.refreshToken.token);
            TokenHelper.setAccessTokenPayload(response.accessToken.payload);
        }
    }
    static getHashedPass(keyPem, password) {
        const pk = Forge.pki.publicKeyFromPem(keyPem);
        const randomWordArray = new Forge.util.ByteBuffer(password, 'utf8').toString('binary');
        return Forge.util.encode64(pk.encrypt(randomWordArray, 'RSA-OAEP', { md: Forge.md.sha256.create() }));
    }
    signUp(inviteToken, user, key = '') {
        return this.http.post(`${this.baseUrl}/users?invite_id=${inviteToken}`, { ...user, action: UsersAction.SIGNUP }, { params: { k: key } });
    }
    getHashSalt() {
        return this.http.post(`${this.baseUrl}/users?getHash`, { action: UsersAction.GET_HASH_SALT });
    }
    login(loginInput) {
        return this.getHashSalt().pipe(switchMap((response) => {
            const password = UsersGatewayService.getHashedPass(response.message, loginInput.password);
            let newPassword = '';
            if (loginInput.newPassword) {
                newPassword = UsersGatewayService.getHashedPass(response.message, loginInput.newPassword);
            }
            return this.http.post(`${this.baseUrl}/users?login`, { ...loginInput, password, newPassword, action: UsersAction.LOGIN });
        }), tap(UsersGatewayService.setLocalStorageItems));
    }
    completeMFAChallenge(mfaChallenge) {
        return this.http.post(`${this.baseUrl}/users?mfa`, { ...mfaChallenge, action: UsersAction.MFA_CHALLENGE })
            .pipe(tap(UsersGatewayService.setLocalStorageItems));
    }
    getCurrentMFAMethod() {
        return this.http.post(`${this.baseUrl}/users/secured`, { action: UsersAction.MFA_OPTIONS });
    }
    associateSoftwareMFA() {
        return this.http.post(`${this.baseUrl}/users/secured`, { action: UsersAction.ASSOCIATE_SOFTWARE_MFA });
    }
    updateUserAttribute(input) {
        return this.http.post(`${this.baseUrl}/users/secured`, { ...input, action: UsersAction.UPDATE_USER_ATTRIBUTE });
    }
    verifyUserAttribute(input) {
        return this.http.post(`${this.baseUrl}/users/secured`, { ...input, action: UsersAction.VERIFY_USER_ATTRIBUTE });
    }
    getAttributeVerificationCode(attributeName) {
        return this.http.post(`${this.baseUrl}/users/secured`, { attributeName, action: UsersAction.GET_ATTRIBUTE_VERIFICATION_CODE });
    }
    verifySoftwareMFA(userCode) {
        return this.http.post(`${this.baseUrl}/users/secured`, { userCode, action: UsersAction.VERIFY_SOFTWARE_MFA });
    }
    setMFAPreference(mfaPreference) {
        return this.http.post(`${this.baseUrl}/users/secured`, { mfaPreference, action: UsersAction.MFA_PREFERENCE });
    }
    forgotPasswordInit(input) {
        return this.http.post(`${this.baseUrl}/users?forgot`, { ...input, action: UsersAction.FORGOT_PASSWORD_INITIATE });
    }
    resendTempPassword(input) {
        return this.http.post(`${this.baseUrl}/users?forgot`, { ...input, action: UsersAction.RESEND_TEMP_PASSWORD });
    }
    forgotPasswordConfirm(input) {
        return this.getHashSalt().pipe(switchMap((response) => {
            const newPassword = UsersGatewayService.getHashedPass(response.message, input.newPassword);
            return this.http.post(`${this.baseUrl}/users?forgot`, { ...input, newPassword, action: UsersAction.FORGOT_PASSWORD_CONFIRM });
        }));
    }
    changePassword(input) {
        return this.getHashSalt().pipe(switchMap((response) => {
            const oldPassword = UsersGatewayService.getHashedPass(response.message, input.oldPassword).toString();
            const newPassword = UsersGatewayService.getHashedPass(response.message, input.newPassword).toString();
            return this.http.post(`${this.baseUrl}/users/secured`, { ...input, oldPassword, newPassword, action: UsersAction.CHANGE_PASSWORD });
        }));
    }
    refreshToken(refreshToken) {
        return this.http.post(`${this.baseUrl}/users/secured?refreshToken`, { ...refreshToken, action: UsersAction.REFRESH_SESSION })
            .pipe(tap(UsersGatewayService.setLocalStorageItems));
    }
    getCurrentUser() {
        return this.http.post(`${this.baseUrl}/users/secured`, { action: UsersAction.GET_CURRENT_USER });
    }
    globalSignOut() {
        return this.http.post(`${this.baseUrl}/users/secured`, { action: UsersAction.GLOBAL_LOG_OUT })
            .pipe(tap(() => TokenHelper.clearLocalStorage()));
    }
    refreshSession() {
        return new Promise((resolve, reject) => {
            if (!TokenHelper.shouldRefreshToken()) {
                const token = TokenHelper.getAccessTokenPayload();
                resolve(token ? token.exp : 0);
                return;
            }
            this.refreshToken({
                refreshToken: TokenHelper.getRefreshToken()
            }).pipe(catchError((response) => {
                reject(response);
                return throwError(response);
            }))
                .subscribe(() => {
                const token = TokenHelper.getAccessTokenPayload();
                resolve(token ? token.exp : 0);
            });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: UsersGatewayService, deps: [{ token: EnvironmentConfig }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: UsersGatewayService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: UsersGatewayService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: EnvironmentConfig }, { type: i2.HttpClient }]; } });

class BcAuthWebModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('BcAuthWebModule is already loaded. Import it once in the AppModule only');
        }
    }
    static forRoot(config) {
        return {
            ngModule: BcAuthWebModule,
            providers: [
                { provide: EnvironmentConfig, useValue: config }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: BcAuthWebModule, deps: [{ token: BcAuthWebModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.4", ngImport: i0, type: BcAuthWebModule, imports: [HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: BcAuthWebModule, providers: [UsersGatewayService], imports: [HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.4", ngImport: i0, type: BcAuthWebModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [HttpClientModule],
                    exports: [],
                    providers: [UsersGatewayService]
                }]
        }], ctorParameters: function () { return [{ type: BcAuthWebModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/*
 * Public API Surface of bc-auth-web
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BcAuthWebModule, EnvironmentConfig, MFAType, TokenHelper, TokenNames, UserAttributeType, UsersGatewayService };
